/**
 * sl-7vu4: Graduated tiered pricing utilities for seat addons.
 *
 * Types + calculation mirroring Stripe's graduated tiers_mode:
 * each tier applies only to units within its bracket (like income tax brackets).
 *
 * Backend equivalent: payment.resolver.ts → calculateGraduatedTotal()
 */

/** A single pricing tier matching Stripe's graduated tier format */
export interface SeatPricingTier {
  up_to: number | null; // null = infinity (last tier)
  unit_amount: number; // cents per unit in this tier
}

/** Complete tiers config for both billing intervals */
export interface SeatTiersConfig {
  monthly: SeatPricingTier[];
  yearly: SeatPricingTier[];
}

/**
 * Parse seatTiers JSON string from GraphQL into typed config.
 * Returns null if input is null/undefined/invalid.
 */
export function parseSeatTiers(
  json: string | null | undefined
): SeatTiersConfig | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as SeatTiersConfig;
  } catch {
    return null;
  }
}

/**
 * Calculate total cost for a given quantity using graduated tiers.
 *
 * Each tier only applies to units within its bracket:
 * - Tier 1 (up_to: 5, 900¢): seats 1-5 → 5 × 900¢
 * - Tier 2 (up_to: 10, 750¢): seats 6-10 → 5 × 750¢
 * - Tier 3 (up_to: null, 500¢): seats 11+ → remaining × 500¢
 */
export function calculateGraduatedTotal(
  tiers: SeatPricingTier[],
  quantity: number
): number {
  let total = 0;
  let remaining = quantity;
  let prevLimit = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierCapacity =
      tier.up_to === null ? remaining : tier.up_to - prevLimit;
    const unitsInTier = Math.min(remaining, tierCapacity);
    total += unitsInTier * tier.unit_amount;
    remaining -= unitsInTier;
    prevLimit = tier.up_to ?? Infinity;
  }

  return total;
}

/**
 * Get the effective per-unit price for a given quantity (graduated average).
 * Useful for displaying "~X€/seat" when quantity is known.
 * Returns 0 if tiers are empty or quantity is 0.
 */
export function calculateEffectiveUnitPrice(
  tiers: SeatPricingTier[],
  quantity: number
): number {
  if (quantity <= 0 || tiers.length === 0) return 0;
  return Math.round(calculateGraduatedTotal(tiers, quantity) / quantity);
}

/**
 * Get the starting price (first tier's unit_amount).
 * Used for "ab X€/Seat" display when no specific quantity is known.
 */
export function getStartingPrice(tiers: SeatPricingTier[]): number {
  return tiers[0]?.unit_amount ?? 0;
}
